import Phaser from 'phaser'
import {
  Direction,
  PlayerStats,
  PLAYER_BASE_STATS,
  LEVEL_UP_BONUSES,
  EVENTS,
  DamageEvent,
  LevelUpEvent
} from '../types/GameTypes'

export class Player extends Phaser.Physics.Arcade.Sprite {
  private speed: number = 200
  private direction: Direction = 'down'
  private _isMoving: boolean = false
  private _isDead: boolean = false

  // Combat stats
  public stats: PlayerStats

  // Attack timing
  private lastAttackTime: number = 0

  // Spawn point for respawn
  private spawnX: number
  private spawnY: number

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 'citizen')

    // Store spawn point
    this.spawnX = x
    this.spawnY = y

    // Initialize stats
    this.stats = {
      level: 1,
      experience: 0,
      experienceToNextLevel: this.calculateXPForLevel(2),
      maxHp: PLAYER_BASE_STATS.maxHp,
      currentHp: PLAYER_BASE_STATS.maxHp,
      maxMp: PLAYER_BASE_STATS.maxMp,
      currentMp: PLAYER_BASE_STATS.maxMp,
      attack: PLAYER_BASE_STATS.attack,
      defense: PLAYER_BASE_STATS.defense,
      attackSpeed: PLAYER_BASE_STATS.attackSpeed,
      attackRange: PLAYER_BASE_STATS.attackRange
    }

    // Add to scene and enable physics
    scene.add.existing(this)
    scene.physics.add.existing(this)

    // Set up physics body
    const body = this.body as Phaser.Physics.Arcade.Body
    body.setSize(28, 28)
    body.setOffset(2, 4)
    this.setCollideWorldBounds(true)

    // Set depth for proper layering
    this.setDepth(10)

    // Create animations
    this.createAnimations()
  }

  private createAnimations(): void {
    // Static sprite - no animations needed
    // Animations are now no-ops since we use a static citizen image
  }

  public move(velocityX: number, velocityY: number): void {
    if (this._isDead) return

    this.setVelocity(velocityX * this.speed, velocityY * this.speed)

    if (velocityX !== 0 || velocityY !== 0) {
      this._isMoving = true

      // Determine direction based on velocity
      if (Math.abs(velocityX) > Math.abs(velocityY)) {
        this.direction = velocityX > 0 ? 'right' : 'left'
      } else {
        this.direction = velocityY > 0 ? 'down' : 'up'
      }

      // Static sprite - no animation to play
    } else {
      this._isMoving = false
      // Static sprite - no animation to play
    }
  }

  public stopMoving(): void {
    this.setVelocity(0, 0)
    this._isMoving = false
    // Static sprite - no animation to play
  }

  public get isMoving(): boolean {
    return this._isMoving
  }

  public get isDead(): boolean {
    return this._isDead
  }

  public getDirection(): Direction {
    return this.direction
  }

  // Combat methods

  /**
   * Check if the player can attack (cooldown elapsed)
   */
  public canAttack(): boolean {
    if (this._isDead) return false
    const now = this.scene.time.now
    return now - this.lastAttackTime >= this.stats.attackSpeed
  }

  /**
   * Perform an attack (marks cooldown)
   */
  public performAttack(): void {
    this.lastAttackTime = this.scene.time.now
  }

  /**
   * Deal damage to the player
   */
  public takeDamage(damage: number, attackerType: string): number {
    if (this._isDead) return 0

    // Calculate actual damage with defense
    const actualDamage = Math.max(1, damage - this.stats.defense + Phaser.Math.Between(-2, 2))

    // Check for critical hit (10% chance when player receives damage)
    const isCritical = Math.random() < 0.1
    const finalDamage = isCritical ? Math.floor(actualDamage * 1.5) : actualDamage

    this.stats.currentHp = Math.max(0, this.stats.currentHp - finalDamage)

    // Emit damage event
    const damageEvent: DamageEvent = {
      attacker: attackerType as DamageEvent['attacker'],
      target: 'player',
      damage: finalDamage,
      isCritical,
      targetX: this.x,
      targetY: this.y
    }
    this.scene.events.emit(EVENTS.DAMAGE_DEALT, damageEvent)
    this.scene.events.emit(EVENTS.STATS_CHANGED)

    // Flash red when hit
    this.setTint(0xff0000)
    this.scene.time.delayedCall(100, () => {
      if (!this._isDead) {
        this.clearTint()
      }
    })

    // Check for death
    if (this.stats.currentHp <= 0) {
      this.die()
    }

    return finalDamage
  }

  /**
   * Heal the player
   */
  public heal(amount: number): number {
    if (this._isDead) return 0

    const healAmount = Math.min(amount, this.stats.maxHp - this.stats.currentHp)
    this.stats.currentHp += healAmount
    this.scene.events.emit(EVENTS.STATS_CHANGED)

    // Flash green when healed
    if (healAmount > 0) {
      this.setTint(0x00ff00)
      this.scene.time.delayedCall(100, () => {
        this.clearTint()
      })
    }

    return healAmount
  }

  /**
   * Restore mana
   */
  public restoreMana(amount: number): number {
    if (this._isDead) return 0

    const restoreAmount = Math.min(amount, this.stats.maxMp - this.stats.currentMp)
    this.stats.currentMp += restoreAmount
    this.scene.events.emit(EVENTS.STATS_CHANGED)
    return restoreAmount
  }

  /**
   * Handle player death
   */
  public die(): void {
    if (this._isDead) return

    this._isDead = true
    this.stopMoving()

    // Visual death effect
    this.setTint(0x666666)
    this.setAlpha(0.7)

    // Disable physics
    const body = this.body as Phaser.Physics.Arcade.Body
    body.enable = false

    // Emit death event
    this.scene.events.emit(EVENTS.PLAYER_DIED, {
      entity: 'player',
      killedBy: null,
      x: this.x,
      y: this.y
    })
  }

  /**
   * Respawn the player
   */
  public respawn(): void {
    this._isDead = false

    // Reset position to spawn point
    this.setPosition(this.spawnX, this.spawnY)

    // Reset stats
    this.stats.currentHp = this.stats.maxHp
    this.stats.currentMp = this.stats.maxMp

    // Reset visuals
    this.clearTint()
    this.setAlpha(1)

    // Re-enable physics
    const body = this.body as Phaser.Physics.Arcade.Body
    body.enable = true

    // Static sprite - no animation to play

    // Emit respawn event
    this.scene.events.emit(EVENTS.PLAYER_RESPAWNED)
    this.scene.events.emit(EVENTS.STATS_CHANGED)
  }

  // Experience and leveling

  /**
   * Calculate XP required for a given level
   */
  public calculateXPForLevel(level: number): number {
    return Math.floor(100 * level * (1 + level * 0.2))
  }

  /**
   * Add experience points
   */
  public addExperience(amount: number): void {
    if (this._isDead) return

    this.stats.experience += amount
    this.scene.events.emit(EVENTS.EXPERIENCE_GAINED, amount)

    // Check for level up
    while (this.stats.experience >= this.stats.experienceToNextLevel) {
      this.levelUp()
    }

    this.scene.events.emit(EVENTS.STATS_CHANGED)
  }

  /**
   * Level up the player
   */
  private levelUp(): void {
    this.stats.level++

    // Apply stat bonuses
    const hpGain = LEVEL_UP_BONUSES.hp
    const mpGain = LEVEL_UP_BONUSES.mp
    const attackGain = LEVEL_UP_BONUSES.attack
    const defenseGain = LEVEL_UP_BONUSES.defense

    this.stats.maxHp += hpGain
    this.stats.maxMp += mpGain
    this.stats.attack += attackGain
    this.stats.defense += defenseGain

    // Fully heal on level up
    this.stats.currentHp = this.stats.maxHp
    this.stats.currentMp = this.stats.maxMp

    // Calculate next level XP requirement
    this.stats.experienceToNextLevel = this.calculateXPForLevel(this.stats.level + 1)

    // Emit level up event
    const levelUpEvent: LevelUpEvent = {
      newLevel: this.stats.level,
      hpGain,
      mpGain,
      attackGain,
      defenseGain
    }
    this.scene.events.emit(EVENTS.LEVEL_UP, levelUpEvent)

    // Visual effect for level up
    this.setTint(0xffd700)
    this.scene.time.delayedCall(500, () => {
      this.clearTint()
    })
  }

  /**
   * Get distance to a point
   */
  public distanceTo(x: number, y: number): number {
    return Phaser.Math.Distance.Between(this.x, this.y, x, y)
  }
}
